import React, { useState, useEffect } from 'react';
import { Clock, Eye } from 'lucide-react';
import { MarkerInfo, MarkerRecord } from '../../services/api';
import { useResolvedMediaUrl } from '../../hooks/useResolvedMediaUrl';
import MarkerEditor from '../MarkerEditor';
import { PreviewData } from './MarkedTile';

interface MarkedSegmentTileProps {
  record: MarkerRecord;
  previewData?: PreviewData;
  isHovered: boolean;
  onHover: (hovered: boolean) => void;
  onClick: () => void;
  onRemove: () => void;
  onMarkerChange: (next: MarkerRecord | null) => void;
}

const toMarkerInfo = (record: MarkerRecord): MarkerInfo => ({
  marker_id: record.marker_id,
  color: record.color ?? null,
  note: record.note ?? null,
  updated_at: record.updated_at ?? null,
});

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const MarkedSegmentTile: React.FC<MarkedSegmentTileProps> = ({
  record,
  previewData,
  isHovered,
  onHover,
  onClick,
  onRemove,
  onMarkerChange,
}) => {
  const [localMarker, setLocalMarker] = useState(toMarkerInfo(record));

  // Prefer GIF when hovered, thumbnail otherwise
  const previewSource = isHovered && previewData?.gifUrl
    ? previewData.gifUrl
    : previewData?.thumbnailUrl;
  const durablePreviewSource = isHovered && previewData?.gifUrl
    ? previewData.gifGcsUri
    : previewData?.thumbnailGcsUri;
  const {
    resolvedUrl: previewUrl,
    isLoading: isPreviewLoading,
    refreshAfterError,
  } = useResolvedMediaUrl(previewSource, {
    durableSource: durablePreviewSource,
    videoId: record.video_id,
    segmentId: record.segment_id,
  });

  useEffect(() => {
    setLocalMarker({
      marker_id: record.marker_id,
      color: record.color ?? null,
      note: record.note ?? null,
      updated_at: record.updated_at ?? null,
    });
  }, [record.marker_id, record.color, record.note, record.updated_at]);

  const notePreview = (record.note || '').trim();
  const hasTimingInfo = typeof record.segment_start_time === 'number' && typeof record.segment_end_time === 'number';
  const timeRangeLabel = hasTimingInfo
    ? `${formatTime(record.segment_start_time!)} - ${formatTime(record.segment_end_time!)}`
    : null;

  const segmentId = record.segment_id || '';
  const displaySegmentId = segmentId.length > 8 ? `${segmentId.slice(0, 8)}...` : segmentId;

  return (
    <div
      className="marked-tile"
      onClick={onClick}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      {/* Marker Editor */}
      <div className="marked-tile-marker" onClick={(e) => e.stopPropagation()}>
        <MarkerEditor
          marker={localMarker}
          target={{
            entity_type: 'segment',
            index_id: record.index_id || undefined,
            run_id: record.run_id || undefined,
            video_id: record.video_id || undefined,
            segment_id: record.segment_id || undefined,
            segment_start_time: record.segment_start_time ?? undefined,
            segment_end_time: record.segment_end_time ?? undefined,
          }}
          onMarkerChange={(next) => {
            setLocalMarker(next);
          }}
          onRecordChange={onMarkerChange}
        />
      </div>

      {/* Time Badge */}
      {timeRangeLabel ? (
        <div className="entity-badge segment">
          <Clock size={10} />
          {timeRangeLabel}
        </div>
      ) : (
        <div className="entity-badge segment">SEGMENT</div>
      )}

      {/* Preview */}
      <div className="marked-tile-preview">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Segment preview"
            className="marked-tile-image"
            onError={() => refreshAfterError(previewUrl)}
          />
        ) : (
          <div className={`marked-tile-placeholder ${isPreviewLoading ? 'loading' : ''}`}>
            {isPreviewLoading ? (
              <>
                <div className="loading-spinner-small" />
                <span>Loading</span>
              </>
            ) : (
              <>
                <Eye size={32} />
                <span>No Preview</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Info Overlay */}
      <div className="marked-tile-info">
        <div className="marked-tile-title">Segment {displaySegmentId}</div>
        {notePreview && <div className="marked-tile-note">{notePreview}</div>}
        {record.video_id && (
          <div className="marked-tile-meta">Video: {record.video_id.slice(0, 12)}...</div>
        )}
      </div>
    </div>
  );
};

export default MarkedSegmentTile;
